import React, { useEffect, useRef, useState } from 'react';
import { ChevronDownIcon } from '@heroicons/react/24/outline';

const VERSION_STATUS = {
  COMPLETED: { tone: 'ready', label: 'Ready' },
  PROCESSING: { tone: 'busy', label: 'Running' },
  PENDING: { tone: 'busy', label: 'Queued' },
  FAILED: { tone: 'failed', label: 'Failed' },
  DRAFT: { tone: 'draft', label: 'Draft' },
};

/** A finished report stays a bare dot; only states worth reacting to spell themselves out. */
const VersionStatus = ({ status }) => {
  const meta = VERSION_STATUS[status];
  if (!meta) return null;
  return (
    <span className={`report-version-badge report-version-badge-${meta.tone}`} title={meta.label}>
      <span className="report-version-dot" />
      <span className={meta.tone === 'ready' ? 'report-version-sr' : undefined}>{meta.label}</span>
    </span>
  );
};

/** Version dropdown plus the bar's action buttons (passed as children). */
export default function VersionBar({ scopeId, options, selectedId, disabled, onSelect, children }) {
  const [open, setOpen] = useState(false);
  const menuRef = useRef(null);
  const current = options.find((option) => option.id === selectedId) || null;
  const labelId = `report-version-label-${scopeId}`;
  const triggerId = `report-version-${scopeId}`;

  useEffect(() => {
    if (!open) return undefined;
    const onPointerDown = (event) => {
      if (!menuRef.current?.contains(event.target)) setOpen(false);
    };
    const onKeyDown = (event) => {
      if (event.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  return (
    <div className="report-version-bar">
      <span className="report-version-label" id={labelId}>Report version</span>
      <div className="report-version-select" ref={menuRef}>
        <button
          type="button"
          id={triggerId}
          className="report-version-trigger"
          onClick={() => setOpen((prev) => !prev)}
          disabled={disabled}
          aria-haspopup="listbox"
          aria-expanded={open}
          aria-labelledby={`${labelId} ${triggerId}`}
        >
          {current ? (
            <>
              <span className="report-version-name">{current.label}</span>
              <VersionStatus status={current.status} />
            </>
          ) : (
            <span className="report-version-name report-version-placeholder">Pick a report version</span>
          )}
          <ChevronDownIcon className="report-version-chevron" />
        </button>

        {open && (
          <ul className="report-version-menu" role="listbox">
            {options.map((option) => (
              <li key={option.id}>
                <button
                  type="button"
                  role="option"
                  aria-selected={option.id === selectedId}
                  className={`report-version-option${option.id === selectedId ? ' selected' : ''}`}
                  onClick={() => {
                    setOpen(false);
                    onSelect(option.id);
                  }}
                >
                  <span className="report-version-name">{option.label}</span>
                  <VersionStatus status={option.status} />
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
      {children}
    </div>
  );
}
